import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import {
  Table,
  Input,
  Button,
  Tag,
  Dropdown,
  Select,
  Menu,
  Popover,
  Popconfirm,
  Avatar,
  Modal,
  Divider,
  DatePicker
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faClock, faMapMarked } from '@fortawesome/pro-duotone-svg-icons';
import { ClockCircleOutlined } from '@ant-design/icons';
import {
  reloadLeadsAtom,
  userAtom,
  viewLeadAssignedAtom,
  notificationsAtom
} from '../../../../atoms/Atoms';
import MainPage from '../../../shared-components/main-page/MainPage';
import { H1 } from '../../../shared-components/typography/Title';
import {
    getWaitTimeText,
  mapDealType,
  mapLeadProgressColor,
  mapLeadStatus,
  percentage,
  thousand,
  toFormattedLocalTime,
  hasBillFileType,
  toFormattedLocalDate,
  toFormattedTime,
} from '../../../../functions';
import api from '../../../../api/api';

function LeadsManager({ includeBack }) {
  const { t } = useTranslation();
  const { Option } = Select;
  const history = useHistory();
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const setViewAssigned = useSetRecoilState(viewLeadAssignedAtom);
  const [reloadLeads, setReloadLeads] = useRecoilState(reloadLeadsAtom);
  const user = useRecoilValue(userAtom);
  const [loading, setLoading] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searchResult, setSearchResult] = useState(null);
  const [selectedSort, setSelectedSort] = useState(0);
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [selectedOrgCode, setSelectedOrgCode] = useState('');

  function search(value, page) {
    if (loading || !selectedOrgCode || selectedOrgCode?.length === 0) {
      return;
    }
    setLoading(true);
    setLastSearchTerm(value);
    setCurrentPage(page);
    const url = 'leads/search';
    api
      .post(
        url,
        {
          Query: value,
          Size: pageSize,
          Page: page,
          Type: 0,
          Filter: 1,
          OrgReferralCode: selectedOrgCode,
          Sort: selectedSort
        }
      )
      .then((response) => {
        setLoading(false);
        console.log('tt', response.data.data.submissions)
        if (response?.data?.success) {
          setSearchResult({
            ...response.data.data,
            submissions: _.map(response.data.data.submissions, (s) => ({
              ...s,
              hasCloser: _.filter(s?.assignments, (a) => a.ownershipType === 0).length > 0
            }))
          });
        } else {
          setSearchResult({
            ...searchResult,
            users: []
          });
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            },
          ]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          },
        ]);
      });
  }

  function onSearch(value) {
    search(value, 0);
  }

  const onTableChange = (page, size) => {
    if (page - 1 !== currentPage) {
      setCurrentPage(page - 1);
      search(lastSearchTerm, page - 1);
    } else {
      setPageSize(size);
    }
  };

  function getOrganizations() {
    setLoadingOrganizations(true);
    api.get('organizations/user')
      .then((response) => {
        setLoadingOrganizations(false);
        if (response?.data?.success) {
          setOrganizations(response.data.data);
          setSelectedOrgCode(response.data.data[0].referralCode);
        } else {
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: response?.data?.error,
              error: true,
            }
          ]);
        }
      })
      .catch((error) => {
        setLoadingOrganizations(false);
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: error.message,
            error: true,
          }
        ]);
      })
  }

  useEffect(() => {
    if (selectedOrgCode) {
      search(lastSearchTerm, 0);
    }
  }, [selectedOrgCode]);

  useEffect(() => {
    if (reloadLeads) {
      setReloadLeads(false);
      search(lastSearchTerm, currentPage);
    }
  }, [reloadLeads]);

  useEffect(() => {
    getOrganizations();
  }, []);

  return (
    <MainPage includeBack requiresCloser>
      <div className="md:flex mt-4 space-y-2 space-x-2">
        <H1 text={t('unassigned_leads')} className="mr-auto" />
        <Button
          className="ml-auto w-full md:w-auto"
          type="primary"
          disabled={!selectedOrgCode || loadingOrganizations}
          onClick={() => history.push(`/calendar/${selectedOrgCode}`)}
        >
          {t('calendar')}
        </Button>
      </div>
      <div className="lg:flex items-center space-x-2 justify-center">
        <Input.Search
          placeholder={`${t('search')}...`}
          allowClear
          disabled={loadingOrganizations || loading}
          className="mt-4"
          enterButton={t('search')}
          size="large"
          onSearch={(val) => onSearch(val)}
        />
        <Select
          className="h-12 mt-4 w-full md:w-48"
          value={selectedOrgCode}
          disabled={loadingOrganizations || organizations?.length === 0 || organizations?.length === 1}
          onChange={(obj) => setSelectedOrgCode(obj)}
        >
          {_.map(organizations, (o) => (
            <Option value={o.referralCode}>{o.nameEng}</Option>
          ))}
        </Select>
        <Select
          className="h-12 mt-4 w-full md:w-48"
          value={selectedSort}
          disabled={loading}
          onChange={(obj) => setSelectedSort(obj)}
        >
          <Option value={0}>{t('created_on')} <FontAwesomeIcon className="ml-2" icon={faArrowDown} /></Option>
          <Option value={1}>{t('created_on')} <FontAwesomeIcon className="ml-2" icon={faArrowUp} /></Option>
          {/* <Option value={2}>{t('stage')} <FontAwesomeIcon className="ml-2" icon={faArrowDown} /></Option>
          <Option value={3}>{t('stage')} <FontAwesomeIcon className="ml-2" icon={faArrowUp} /></Option> */}
        </Select>
      </div>
      <div className="mt-4">{t('showing')}: {searchResult?.total ?? 0}</div>
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        rowKey="id"
        pagination={{
          pageSize,
          showSizeChanger: true,
          current: currentPage + 1,
          total: searchResult?.total,
          onChange: onTableChange
        }}
        loading={loading || loadingOrganizations}
        dataSource={searchResult?.submissions}
        columns={[
          {
            title: '',
            align: 'center',
            className: 'text-sm w-full md:hidden',
            render: (row) => (
              <div className={`${row.hasCloser ? '' : ''} grid gap-2 grid-cols-2 border border-black rounded-md p-2`}>
                <div className="hidden col-span-2 text-center space-x-2">
                  <span>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
                </div>
                <div className="text-center col-span-2 space-x-2">
                  <Tag color={mapLeadProgressColor(row)}>{t(mapLeadStatus(row.status))} {getWaitTimeText(row, t)}</Tag>
                </div>
                <div className="col-span-2 text-center space-x-2">
                 
                  <div
                    className="text-primaryColor col-span-2 text-semi-bold hover:underline focus:outline-none"
                  >
                    <strong>{t('client')}: </strong>
                    <span>
                      {/* <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" /> */}

                      {row?.submittedBy?.completeName}
                    </span>
                  </div>
                </div>
                <div className="hidden col-span-2 text-center space-x-2">
                  <strong>{t('closer')}: </strong>
                  <>
                    {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
                      <span>
                        <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
                        {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
                      </span>
                    )}
                    {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
                      <span>-</span>
                    )}
                  </>
                </div>
                <div className="col-span-2 text-center space-x-2">
                  <>
                    {row?.setter && (
                      <div>
                        <strong>{t('setter')}: </strong>
                        <span className="">{row?.setter?.completeName}</span>
                      </div>
                    )}
                      {!row?.setter && (
                      <div>
                        {t('self-gen')}
                      </div>
                    )}         
                  </>
                </div>
                <div className="col-span-2 text-center space-x-2">
                  <p><span className="font-bold">{t('type')}:</span> {t(mapDealType(row.dealType))}</p>
                </div>
                <div className="col-span-2 text-center space-x-2">
                  <p><FontAwesomeIcon icon={faClock} className="text-primaryColor mr-2" /><span className="font-bold">{row.appointments?.length > 0 ? `${toFormattedLocalTime(row.appointments[0].appointmentDate)}` : 'No Appointment' }</span></p>
                </div>
                <div className="col-span-2 text-center space-x-2"><p className="font-semibold">{t('bill_uploaded')}: {`${hasBillFileType(row?.files, t) ? 'Yes' : 'No'}`}</p></div>
                <div className="col-span-2 space-x-2">                  
                  <span id="municipality-label">
                    {row.municipality ? row.municipality : '-'}
                  </span>
                  {row.location?.lat !== 0 && row.location?.lon !== 0 && (
                    <a
                      href={`https://www.google.com/maps?q=${row.location.lat},${row.location.lon}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2"
                      aria-label={`View location on map for ${row.municipality || 'this place'}`}
                    >
                      <FontAwesomeIcon icon={faMapMarked} className="text-primaryColor mr-2" />
                    </a>
                  )}
                  {row.location?.lat === 0 || row.location?.lon === 0 && (
                       <FontAwesomeIcon icon={faMapMarked} className="text-black" />
                  )}
                  {row.location?.lat === row.location?.lon && (
                       <p>({t('no_pin_available')})</p>
                  )}
                </div>
                {/* <div className="text-center col-span-2 space-x-2">
                  <Tag color={`${row.assignments.length === 0 ? 'red' : 'green'}`}>{`${row.assignments.length === 0 ? t('available') : t('assigned')}`}</Tag>
                </div> */}               
                <div className="col-span-2 space-x-2 flex justify-center">
                    <Button
                      id="viewassigned"
                      className="text-xs flex items-center w-auto"
                      type="primary"
                      color="green-200"
                      size="small"
                      onClick={() => {
                        setViewAssigned({
                          ...row,
                          referralCode: selectedOrgCode
                        });
                      }}
                    >
                      {t('assign')}
                    </Button>
                </div>
              </div>
            ),
          },
          {
            title: t('created_on'),
            width: 200,
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span className={`${row.hasCloser ? '' : ''}`}>{row.createdAt ? toFormattedLocalTime(row.createdAt) : '-'}</span>
            ),
          },
          {
            title: t('client'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <span className={`${row.hasCloser ? '' : ''}`}>
                {/* <Avatar src={row?.submittedBy?.avatar} className="bg-primaryColor h12 w-8 mr-2" /> */}
                {row?.submittedBy?.completeName}
              </span>
            ),
          },
          // {
          //   title: t('closer'),
          //   align: 'left',
          //   className: 'text-sm hidden md:table-cell',
          //   render: (row) => (
          //     <div className={`${row.hasCloser ? '' : 'text-red-400'}`}>
          //       {_.filter(row?.assignments, (a) => a.ownershipType === 0).length > 0 && (
          //         <span>
          //           <Avatar src={_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.avatar} className="bg-primaryColor h12 w-8 mr-2" />
          //           {_.filter(row?.assignments, (a) => a.ownershipType === 0)[0].assignedTo?.completeName}
          //         </span>
          //       )}
          //       {_.filter(row?.assignments, (a) => a.ownershipType === 0).length === 0 && (
          //         <span>-</span>
          //       )}
          //     </div>
          //   ),
          // },
          {
            title: t('setter'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <div className={`${row.hasCloser ? '' : ''}`}>
                {row?.setter && (
                  <span>
                    <Avatar src={row?.setter?.avatar} className=" bg-primaryColor h12 w-8 mr-2" />
                    {row?.setter?.completeName}
                  </span>
                )}
                {!row?.setter && (
                  <span>{t('self-gen')}</span>
                )}
              </div>
            ),
          },
          {
            title: t('appointment'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <p className="text-center">{row.appointmentDateTime ? toFormattedLocalTime(row.appointmentDateTime) : '-'}</p>
            ),
          },
          {
            title: t('deal_type'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <p className={`${row.hasCloser ? '' : ''}`}>{t(mapDealType(row.dealType))}</p>
            ),
          },
          {
            title: t('municipality'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              // <span className={`${row.hasCloser ? '' : ''}`}>{row.municipality ? row.municipality : '-'}</span>
              <div>
                <span id="municipality-label">
                  {row.municipality ? row.municipality : '-'}
                </span>
                {row.location?.lat !== 0 && row.location?.lon !== 0 && (
                  <a
                    href={`https://www.google.com/maps?q=${row.location.lat},${row.location.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2"
                    aria-label={`View location on map for ${row.municipality || 'this place'}`}
                  >
                    <FontAwesomeIcon icon={faMapMarked} className="text-primaryColor mr-2" />
                  </a>
                )}
                {row.location?.lat === 0 || row.location?.lon === 0 && (
                  <FontAwesomeIcon icon={faMapMarked} className="text-black" />
                )}
                {row.location?.lat === row.location?.lon && (
                  <p>({t('no_pin_available')})</p>
                )}
              </div>
            ),
          },
          {
            title: t('property_owner'),
            align: 'center',
            className: 'text-sm hidden',
            render: (row) => (
              <Tag color={`${row.isPropertyOwner ? 'green' : 'red'}`}>{row.isPropertyOwner ? t('yes') : t('no')}</Tag>
            ),
          },
          {
            title: t('bill'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <Tag color={`${hasBillFileType(row?.files, t) ? 'green' : 'red'}`}>{`${hasBillFileType(row?.files, t) ? t('yes') : t('no')}`}</Tag>
            ),
          },
          {
            title: t('stage'),
            align: 'left',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <Tag color={mapLeadProgressColor(row)}>{t(mapLeadStatus(row.status))} {getWaitTimeText(row, t)}</Tag>
            ),
          },
          {
            title: '',
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <div className="space-x-2 space-y-2">
                <Button
                  id="viewassigned"
                  className="text-xs flex items-center"
                  type="primary"
                  size="small"
                  onClick={() => {
                    setViewAssigned({
                      ...row,
                      referralCode: selectedOrgCode
                    });
                  }}
                >
                  {t('assign')}
                </Button>
              </div>
            ),
          },
        ]}
      />
    </MainPage>
  );
}

export default LeadsManager;
